import { createAction } from "redux-actions";
import {
  CATEGORY_DETAILS_LOADED,
  CATEGORY_LOADED,
  CURATED_CATEGORY_TREATMENTS_LOADED,
  SIBLING_CATEGORIES_LOADED,
  CATEGORY_FAQS_LOADED,
  EARLY_ACCESS_TO_EXCLUSIVES,
  EXCLUDED_SIBLING_CATEGORIES_PARENTS,
  RELATED_CATEGORIES_LOADED
} from "../constants";
import logger from "../../../../lib/logger";
import { getCategoryDetails, getCategory } from "../../../../lib/api/category";
import { getCuratedCategoryTreatments } from "../../../../lib/api/curatedCategory";
import getDisplayableChildLinkCountForCategory from "./getDisplayableChildLinkCountForCategory";
import { getCuratedCategoryChannel } from "../../config/selectors";

export function loadCategoryDetails({
  country,
  language,
  query,
  uuid,
  xSiteOrigin,
  loyaltyTier
}) {
  return async dispatch => {
    try {
      const response = await getCategoryDetails({
        country,
        language,
        query,
        uuid,
        xSiteOrigin,
        loyaltyTier
      });
      dispatch(categoryDetailsLoaded(response));

      if (response?.categoryDetails?.relatedCategories?.length) {
        dispatch(
          relatedCategoriesLoaded({
            relatedCategories: response.categoryDetails.relatedCategories || []
          })
        );
      }
    } catch (error) {
      error.message = `getCategoryDetails failed, and caught error: ${error.message}`;
      logger.error(error);
      await dispatch(categoryDetailsLoaded(error));
      throw error;
    }
  };
}

export function loadCategoryFaqs({ language, query }) {
  return (dispatch, getState) => {
    const state = getState();

    const faqs = SERVER
      ? {
          [query.cid]: require("../../../data/faqs").getFaqs(
            query.cid,
            language
          )
        }
      : state.category.faqs;

    dispatch(categoryFaqsLoaded(faqs));
  };
}

export function loadCategory({ country, language, query, uuid, xSiteOrigin }) {
  return async dispatch => {
    try {
      dispatch(siblingCategoriesLoaded({ siblingCategories: [] }));
      dispatch(relatedCategoriesLoaded({ relatedCategories: [] }));

      const displayableChildLinkCount = getDisplayableChildLinkCountForCategory(
        {
          categoryId: query.cid
        }
      );

      const response = await getCategory({
        country,
        language,
        query,
        uuid,
        xSiteOrigin,
        displayableChildLinkCount
      });

      dispatch(categoryLoaded(response));

      await loadSiblingCategoriesIfNeeded({
        dispatch,
        response,
        country,
        language,
        query,
        uuid,
        xSiteOrigin
      });
    } catch (error) {
      logger.error(error);
      dispatch(categoryLoaded(error));
      throw error;
    }
  };
}

async function loadSiblingCategoriesIfNeeded({
  dispatch,
  response,
  country,
  language,
  query,
  uuid,
  xSiteOrigin
}) {
  if (shouldLoadSiblingCategories(response, query)) {
    const parentCid = getParentCategoryId(response);
    await fetchAndDispatchSiblingCategories({
      dispatch,
      parentCid,
      country,
      language,
      query,
      uuid,
      xSiteOrigin
    });
  }
}

function shouldLoadSiblingCategories(response) {
  const hasChildCategories = response.categories?.categories?.length > 0;
  const parentCid = getParentCategoryId(response);
  const isEarlyAccessCategory =
    response.categories?.loyaltyBenefitType === EARLY_ACCESS_TO_EXCLUSIVES;

  const isExcludedParentCategory = EXCLUDED_SIBLING_CATEGORIES_PARENTS.includes(
    parentCid
  );

  return (
    !hasChildCategories &&
    !isExcludedParentCategory &&
    parentCid &&
    !isEarlyAccessCategory
  );
}

function getParentCategoryId(response) {
  const breadcrumb = response.categories?.breadcrumb || [];

  return breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].id : null;
}

async function fetchAndDispatchSiblingCategories({
  dispatch,
  parentCid,
  country,
  language,
  query,
  uuid,
  xSiteOrigin
}) {
  const parentResponse = await getCategory({
    country,
    language,
    query: { cid: parentCid },
    uuid,
    xSiteOrigin
  });

  const siblingPills = processSiblingPills(parentResponse, query.cid);
  dispatch(siblingCategoriesLoaded({ siblingCategories: siblingPills }));
}

function processSiblingPills(parentResponse, currentCid) {
  const siblingPills = parentResponse?.categories?.categories?.map(
    ({ friendlyName, linkUrl, id }) => ({
      friendlyName,
      linkUrl,
      isCurrentCategory: currentCid == id
    })
  );

  if (Array.isArray(siblingPills)) {
    return siblingPills
      .filter(({ linkUrl }) => linkUrl && !linkUrl.includes("ctas/"))
      .slice(0, 15);
  }

  return [];
}

export function loadCuratedCategoryTreatments({ query, country }) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const channel = getCuratedCategoryChannel(state);

      const treatments = await getCuratedCategoryTreatments({
        query,
        country,
        channel
      });
      dispatch(curatedCategoryTreatmentsLoaded(treatments));
    } catch (error) {
      error.message = `getCuratedCategoryTreatments failed, and caught error: ${error.message}`;
      logger.error(error);
      await dispatch(curatedCategoryTreatmentsLoaded());
    }
  };
}

export const categoryDetailsLoaded = createAction(
  CATEGORY_DETAILS_LOADED,
  response => ({
    ...response.categoryDetails
  })
);

export const categoryLoaded = createAction(CATEGORY_LOADED, response => ({
  ...(SERVER
    ? {
        gender: require("../../../../server/render/common/breadcrumb").getGenderFromBreadcrumb(
          response.categories?.breadcrumb || []
        )
      }
    : {}),
  ...response.categories
}));

export const siblingCategoriesLoaded = createAction(
  SIBLING_CATEGORIES_LOADED,
  response => response.siblingCategories
);

export const relatedCategoriesLoaded = createAction(
  RELATED_CATEGORIES_LOADED,
  response => response.relatedCategories
);

export const categoryFaqsLoaded = createAction(
  CATEGORY_FAQS_LOADED,
  faqs => faqs
);

export const curatedCategoryTreatmentsLoaded = createAction(
  CURATED_CATEGORY_TREATMENTS_LOADED,
  treatments => treatments
);
